import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendOtpAPI, verifyOtpAPI } from "./request-quote.service";
import { createQuoteAPI } from "../Home-Page/home-page.component.service";
import { BASE_URL } from "../../constants/api.constants";

const emptyItem = () => ({ partNumber: "", quantity: "", targetPrice: "" });

const cellInput = {
  width: "100%",
  height: 36,
  border: "1px solid var(--stroke-1)",
  borderRadius: "var(--radius-md)",
  padding: "0 10px",
  font: "var(--fw-regular) var(--t-body1) var(--font-ui)",
  color: "var(--fg-1)",
  background: "#fff",
};

const maskEmail = (e = "") => {
  const [u, d] = e.split("@");
  if (!d) return e;
  return (u ? u[0] : "") + "***@" + d;
};
const maskMobile = (m = "") => (m ? "••••••" + m.slice(-4) : "");

const RequestQuoteComponent = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("details"); // details | verify | done
  const [items, setItems] = useState([emptyItem()]);
  const [contact, setContact] = useState({ name: "", email: "", company: "", mobile: "" });
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // OTP step
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);

  const [refId, setRefId] = useState("");

  // resend countdown
  useEffect(() => {
    if (step !== "verify" || resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [step, resendTimer]);

  const updateItem = (i, field, value) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  };
  const addRow = () => setItems((prev) => [...prev, emptyItem()]);
  const removeRow = (i) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const handleContact = (e) => {
    const { name, value } = e.target;
    setContact((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const e = {};
    const hasPart = items.some((it) => it.partNumber.trim() && String(it.quantity).trim());
    if (!hasPart) e.items = "Add at least one part with a quantity.";
    if (!contact.name.trim()) e.name = "Name is required.";
    if (!contact.email.trim()) e.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(contact.email)) e.email = "Enter a valid email.";
    if (!contact.mobile.trim()) e.mobile = "Mobile is required.";
    else if (!/^[0-9]{10}$/.test(contact.mobile)) e.mobile = "Enter a valid 10-digit mobile number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const sendOtp = async () => {
    return sendOtpAPI({ email: contact.email.trim(), mobile: contact.mobile.trim() });
  };

  const handleContinue = async () => {
    if (!validate()) return;
    setSending(true);
    setErrors((prev) => ({ ...prev, otp: undefined }));
    const resp = await sendOtp();
    setSending(false);
    if (resp.status) {
      setOtpCode("");
      setOtpError("");
      setResendTimer(60);
      setStep("verify");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setErrors((prev) => ({ ...prev, otp: resp.message || "Could not send the code." }));
    }
  };

  const handleResend = async () => {
    setOtpError("");
    const resp = await sendOtp();
    if (resp.status) setResendTimer(60);
    else setOtpError(resp.message || "Could not resend the code.");
  };

  const buildExportList = () =>
    items
      .filter((it) => it.partNumber.trim())
      .map((it, i) => ({
        sno: i + 1,
        part_number: it.partNumber,
        qty: it.quantity,
        part_match: "-",
        quoted_mpn: "-",
        manufacturer: "-",
        description: "-",
        availability: "-",
        min_mul: "-",
        suggested_qty: it.quantity,
        lead_time: "-",
        unit_price: it.targetPrice || "-",
        ext_price: "-",
      }));

  const handleVerify = async () => {
    if (!otpCode.trim()) {
      setOtpError("Enter the 6-digit code we sent you.");
      return;
    }
    setVerifying(true);
    setOtpError("");

    const resp = await verifyOtpAPI({
      email: contact.email.trim(),
      mobile: contact.mobile.trim(),
      code: otpCode.trim(),
    });
    if (!resp.status) {
      setVerifying(false);
      setOtpError(resp.message || "Invalid code. Please try again.");
      return;
    }

    // Verified — generate the quote document.
    const exportList = buildExportList();
    const quote = await createQuoteAPI({ exportList });
    setVerifying(false);

    if (quote.status) {
      if (quote.filePath) {
        // The backend prepends a (mis)configured APP_URL to the file path, which
        // can produce an invalid URL. Rebuild a valid absolute URL from the app's
        // BASE_URL + the public "app-source/…" path, and open it in a new tab.
        let href = quote.filePath;
        const idx = href.indexOf("app-source/");
        if (idx >= 0) href = BASE_URL.replace(/\/+$/, "") + "/" + href.slice(idx);
        const a = document.createElement("a");
        a.href = href;
        a.download = quote.fileName || "quote.xlsx";
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Log the full request for any downstream use.
      console.log("RFQ verified & quote generated:", { items: exportList, contact, notes });
      setRefId("RFQ-" + String(Date.now()).slice(-6));
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setOtpError(quote.message || "Verified, but the quote could not be generated. Please try again.");
    }
  };

  const resetAll = () => {
    setStep("details");
    setItems([emptyItem()]);
    setContact({ name: "", email: "", company: "", mobile: "" });
    setNotes("");
    setErrors({});
    setOtpCode("");
    setOtpError("");
  };

  // ---------------- STEP 3: SUCCESS ----------------
  if (step === "done") {
    return (
      <div className="ds-container" style={{ display: "flex", justifyContent: "center", paddingBlock: "64px 96px" }}>
        <div className="ds-state" style={{ maxWidth: 540 }}>
          <span className="ico" style={{ background: "var(--brand-teal-50)", color: "var(--brand-teal)" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <h3 style={{ font: "var(--fw-bold) 24px var(--font-display)", color: "var(--brand-ink)" }}>
            Quote request confirmed
          </h3>
          <p>
            Thanks{contact.name ? `, ${contact.name.split(" ")[0]}` : ""}! Your request{" "}
            <b className="ds-mono" style={{ color: "var(--brand-violet)" }}>#{refId}</b> is verified and your
            quote has been downloaded.
          </p>
          <div className="ds-row" style={{ justifyContent: "center", marginTop: 8 }}>
            <button className="ds-btn ds-btn-secondary" onClick={resetAll}>
              Request another quote
            </button>
            <button className="ds-btn ds-btn-primary" onClick={() => navigate("/")}>
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- STEP 2: VERIFY OTP ----------------
  if (step === "verify") {
    return (
      <div className="ds-container" style={{ display: "flex", justifyContent: "center", paddingBlock: "56px 96px" }}>
        <div className="ds-panel ds-panel-p" style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
          <span
            style={{
              width: 64, height: 64, borderRadius: "50%", background: "var(--brand-violet-50)",
              color: "var(--brand-violet)", display: "inline-flex", alignItems: "center",
              justifyContent: "center", marginBottom: 16,
            }}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </span>

          <p className="ds-eyebrow">Step 2 of 2</p>
          <h1 style={{ fontSize: 24 }}>Verify it's you</h1>
          <p className="ds-muted" style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.6 }}>
            We sent a 6-digit code to<br />
            <b style={{ color: "var(--fg-1)" }}>{maskEmail(contact.email)}</b> and{" "}
            <b style={{ color: "var(--fg-1)" }}>{maskMobile(contact.mobile)}</b>
          </p>

          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="••••••"
            autoFocus
            style={{
              width: "100%", maxWidth: 280, height: 60, margin: "24px auto 0", display: "block",
              textAlign: "center", fontSize: 28, letterSpacing: "0.4em", fontFamily: "var(--font-mono)",
              color: "var(--fg-1)", background: "#fff", border: "1px solid var(--stroke-1)",
              borderBottom: "2px solid var(--stroke-1)", borderRadius: "var(--radius-md)", outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "var(--brand-violet)")}
            onBlur={(e) => (e.target.style.borderColor = "var(--stroke-1)")}
          />
          {otpError && <div style={{ color: "var(--danger)", fontSize: 13, marginTop: 10 }}>{otpError}</div>}

          <button
            className="ds-btn ds-btn-primary ds-btn-block ds-btn-lg"
            style={{ marginTop: 20 }}
            onClick={handleVerify}
            disabled={verifying}
          >
            {verifying ? "Verifying…" : "Verify & get quote"}
          </button>

          <div className="ds-muted" style={{ marginTop: 16, fontSize: 13 }}>
            {resendTimer > 0 ? (
              <>Didn't get it? Resend in {resendTimer}s</>
            ) : (
              <>
                Didn't get it?{" "}
                <button className="ds-link" style={{ background: "none", border: 0, cursor: "pointer", padding: 0 }} onClick={handleResend}>
                  Resend code
                </button>
              </>
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              className="ds-link"
              style={{ background: "none", border: 0, cursor: "pointer", padding: 0, fontSize: 13 }}
              onClick={() => setStep("details")}
            >
              ← Edit details
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- STEP 1: DETAILS ----------------
  return (
    <div className="ds-container">
      <nav className="ds-breadcrumbs">
        <span onClick={() => navigate("/")} role="button">Home</span>
        <span className="sep">/</span>
        <span className="current">Request a Quote</span>
      </nav>

      <div className="ds-page-head" style={{ maxWidth: 640 }}>
        <p className="ds-eyebrow">Step 1 of 2</p>
        <h1>Request a quote</h1>
        <p className="sub">
          Tell us the parts and quantities you need. We'll verify your contact, then source live pricing
          across our distributors and generate your quote.
        </p>
      </div>

      <div className="ds-stack" style={{ paddingBottom: 56 }}>
        {/* Parts */}
        <div className="ds-panel ds-panel-p ds-stack">
          <div className="ds-head-row">
            <h3>Parts to quote</h3>
            <span className="ds-muted">{items.length} line{items.length === 1 ? "" : "s"}</span>
          </div>
          <div className="ds-table-wrap">
            <table className="ds-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }} className="num">#</th>
                  <th>Part number / MPN</th>
                  <th style={{ width: 150 }}>Quantity</th>
                  <th style={{ width: 170 }}>Target price (₹)</th>
                  <th style={{ width: 48 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td className="num">{i + 1}</td>
                    <td>
                      <input
                        style={{ ...cellInput, fontFamily: "var(--font-mono)" }}
                        value={it.partNumber}
                        onChange={(e) => updateItem(i, "partNumber", e.target.value)}
                        placeholder="e.g. STM32F407VGT6"
                      />
                    </td>
                    <td>
                      <input
                        style={cellInput}
                        type="number"
                        min="1"
                        value={it.quantity}
                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                        placeholder="Qty"
                      />
                    </td>
                    <td>
                      <input
                        style={cellInput}
                        value={it.targetPrice}
                        onChange={(e) => updateItem(i, "targetPrice", e.target.value)}
                        placeholder="Optional"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => removeRow(i)}
                        aria-label="Remove row"
                        title="Remove"
                        disabled={items.length === 1}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {errors.items && <div style={{ color: "var(--danger)", fontSize: 12 }}>{errors.items}</div>}
          <div>
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={addRow}>
              + Add another part
            </button>
          </div>
        </div>

        {/* Contact */}
        <div className="ds-panel ds-panel-p">
          <h3 className="ds-mb-l">Your details</h3>
          <div className="ds-grid-2" style={{ gap: 16 }}>
            <div className="ds-field">
              <label>Name *</label>
              <div className="ds-input">
                <input name="name" value={contact.name} onChange={handleContact} placeholder="Your name" />
              </div>
              {errors.name && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.name}</span>}
            </div>
            <div className="ds-field">
              <label>Work email *</label>
              <div className="ds-input">
                <input name="email" type="email" value={contact.email} onChange={handleContact} placeholder="you@company.com" />
              </div>
              {errors.email && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.email}</span>}
            </div>
            <div className="ds-field">
              <label>Company</label>
              <div className="ds-input">
                <input name="company" value={contact.company} onChange={handleContact} placeholder="Company name" />
              </div>
            </div>
            <div className="ds-field">
              <label>Mobile * <span className="ds-muted" style={{ fontWeight: 400 }}>(10 digits)</span></label>
              <div className="ds-input">
                <input name="mobile" type="tel" maxLength={10} value={contact.mobile} onChange={handleContact} placeholder="9902099933" />
              </div>
              {errors.mobile && <span style={{ color: "var(--danger)", fontSize: 12 }}>{errors.mobile}</span>}
            </div>
          </div>
          <div className="ds-field" style={{ marginTop: 16 }}>
            <label>Notes (lead time, packaging, alternates…)</label>
            <textarea
              className="ds-textarea"
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else we should know?"
            />
          </div>
        </div>

        {errors.otp && <div style={{ color: "var(--danger)", fontSize: 13 }}>{errors.otp}</div>}

        <div className="ds-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="ds-btn ds-btn-primary ds-btn-lg" onClick={handleContinue} disabled={sending}>
            {sending ? "Sending code…" : "Continue to verification →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestQuoteComponent;
